// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { localize } from '@ta/common.i18n';
import type { NavigateOptions, Route } from '@ta/common.routing';
import type { ViewportCategory } from '@ta/common.state';
import { ReadMore } from '@ta/common.text';
import { getVideoTrackingModule } from '@ta/media.common';
import { RouteConsumer } from '@ta/common.routing';
import { InteractionLogger } from '@ta/tracking.interactions';
import { Icon } from '@ta/styleguide.icon';
import { Linkify } from '@ta/social.links';
import { MemberEventOnObjectBlock } from '@ta/social.member-event';
import Storage from '@ta/common.sessionstorage';

import type { Sponsorship } from '@ta/social.section-core';
import { CardSection } from '@ta/social.section-core';
import VideoPlayerAndSidebar, {
  isVideoAvailable,
  DEFAULT_MAX_VIDEO_HEIGHT,
} from './VideoPlayerAndSidebar';

import styles from './VideoSection.less';
import type { VideoPlayerAndSidebarProps } from './VideoPlayerAndSidebar';

export const FULLSCREEN_FROM_FEED_DATE_LOCAL_STORAGE_KEY = 'VideoFullscreenFromFeedDate';

export type VideoSectionProps = {|
  /** The member currently viewing the card */
  member: any,
  /** The currently logged in user's id */
  loggedInUserId?: string,
  /** The actor for the url */
  actorForUrl?: any,
  /** Controls display options based on how many objects we are displaying  */
  isSingleObject?: boolean,
  /** Is the card being embedded (in a repost for example) */
  isEmbedded?: boolean,
  /** Optional clusterId if the card is in a cluster */
  clusterId?: string,
  /** Optional sponsorship for the video */
  sponsorship?: Sponsorship,
  /** SocialStatisticsBar to be displayed in card */
  socialStatisticsBar?: React.Node,
  /** SocialActionBar to be displayed in card */
  socialActionBar?: React.Node,
  ...VideoPlayerAndSidebarProps,
|};

type ConnectedVideoSectionProps = {|
  ...VideoSectionProps,
  viewportCategory: ViewportCategory,
|};

type Props = {|
  ...ConnectedVideoSectionProps,
  route: any,
  navigate: (Route, options?: NavigateOptions) => any,
  isOpen: boolean,
|};

const VideoSection = (props: Props) => {
  const {
    member,
    actor,
    video,
    isSingleObject,
    isEmbedded,
    viewportCategory,
    isOpen,
    navigate,
    route,
    actorForUrl,
    clusterId,
    maxVideoHeight,
    sponsorship,
    socialStatisticsBar,
    socialActionBar,
  } = props;

  const { object } = video;

  const topNote = isVideoAvailable(video) ? (
    undefined
  ) : (
    <CardSection.Content key="topNote">
      <div className={styles.video_not_available_message}>
        {localize('upload_video_processing_copy')}
      </div>
    </CardSection.Content>
  );

  const header = (
    <MemberEventOnObjectBlock
      objectEventType="onVideo"
      object={video.object}
      member={member}
      actor={actor}
      deleteObject={props.deleteObject}
      untagObject={props.untagObject}
      loggedInUserId={props.loggedInUserId}
      isEmbedded={isEmbedded}
    />
  );

  const MAX_CAPTION_LENGTH = 160;

  const cta = () => (
    <div className={styles.ctaLine}>
      <span className={styles.cta}>{localize('common_Read_more') /* Read more */}</span>
      <Icon name="caret-down" />
    </div>
  );

  // in card && playing -> modal
  const videoOnClick = isOpen
    ? undefined
    : (paused, play) => {
        if (paused) {
          // when we navigate to Video_<id> this component will change its style and show the video in modal
          // problem is that SingleSection is also listening for route changes and loads the video, so we have the video modal duplicated
          // here we set the timestamp to sessionstorage so that SingleSection can read it and do not load the video
          Storage.set(FULLSCREEN_FROM_FEED_DATE_LOCAL_STORAGE_KEY, Date.now());
          navigate({
            ...route,
            modal: `Video_${video.object.id}`,
            username: (actorForUrl && actorForUrl.username) || video.object.userProfile.username,
          });
          play();
        }
      };

  const videoTrackingModule = getVideoTrackingModule(route);
  const content = [
    <CardSection.Content fullWidth key="video">
      <VideoPlayerAndSidebar
        actor={actor}
        video={video}
        isOpen={isOpen}
        deleteObject={props.deleteObject}
        untagObject={props.untagObject}
        onClick={videoOnClick}
        trackingModule={videoTrackingModule}
        onClose={() => navigate(route, { reset: true })}
        maxVideoHeight={maxVideoHeight}
        socialStatisticsBar={socialStatisticsBar}
        socialActionBar={socialActionBar}
      />
    </CardSection.Content>,
    <CardSection.Content key="caption">
      {object.caption && (
        <div className={styles.title_description}>
          <InteractionLogger>
            {logFeedClick => (
              <div className={styles.caption} onClick={e => logFeedClick(e, 'feedClick')}>
                {viewportCategory === 'MOBILE' && object.caption.length > MAX_CAPTION_LENGTH ? (
                  <ReadMore numLinesToShow={3} cta={cta()} ctaNewline>
                    <Linkify
                      text={object.caption}
                      socialReferences={object.socialReferences}
                      useSpan
                      onLinkReferenceClick={(e, url) => {
                        logFeedClick(e, 'linkReferenceClick', {
                          authorUserId: actor.userId,
                          url,
                        });
                      }}
                    />
                  </ReadMore>
                ) : (
                  <Linkify
                    text={object.caption}
                    socialReferences={object.socialReferences}
                    useSpan
                    onLinkReferenceClick={(e, url) => {
                      logFeedClick(e, 'linkReferenceClick', {
                        authorUserId: actor.userId,
                        url,
                      });
                    }}
                  />
                )}
              </div>
            )}
          </InteractionLogger>
        </div>
      )}
    </CardSection.Content>,
  ];

  return (
    <CardSection
      isSingleObject={isSingleObject}
      topNote={topNote}
      header={header}
      content={content}
      socialStatisticsBar={socialStatisticsBar}
      socialActionBar={socialActionBar}
      pois={video.object.orderedLocations}
      clusterId={clusterId}
      sponsorship={sponsorship}
    />
  );
};

const VideoWithRoute = (props: ConnectedVideoSectionProps) => (
  <RouteConsumer>
    {({ route: { modal, ...route }, navigate }) =>
      props.video.object && (
        <VideoSection
          key={`vs_${props.video.object.id}`}
          navigate={navigate}
          route={route}
          isOpen={!props.isEmbedded && modal && modal === `Video_${props.video.object.id}`}
          {...props}
        />
      )
    }
  </RouteConsumer>
);

VideoWithRoute.defaultProps = {
  isSingleObject: false,
  isEmbedded: false,
  member: null,
  actor: null,
  actorForUrl: undefined,
  deleteObject: null,
  untagObject: null,
  clusterId: undefined,
  maxVideoHeight: DEFAULT_MAX_VIDEO_HEIGHT,
};

export default connect<ConnectedVideoSectionProps, VideoSectionProps, _, _, _, _>(state => ({
  viewportCategory: state.meta.device.viewportCategory,
}))(VideoWithRoute);
